import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Scale, HelpCircle } from "lucide-react";

import AppErrorState from "@/components/ui/AppErrorState";
import { getApiErrorMessage } from "@/lib/api";
import { getFinancialReconciliation } from "@/lib/pilotageApi";
import { formatPilotageCurrency, formatPilotageSignedDelta } from "@/lib/pilotageUi";

const MONTH_OPTIONS = [
  { value: "", label: "Toute l'année (12 mois)" },
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

const formatCurrency = (val) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return "0,00 €";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(val);
};

const formatDelta = (val) => {
  if (val === null || val === undefined || Number.isNaN(Number(val)) || Math.abs(val) < 0.01) {
    return "0,00 €";
  }
  const formatted = formatCurrency(Math.abs(val));
  return val > 0 ? `+${formatted}` : `-${formatted}`;
};

export default function FinancialReconciliationPage() {
  const [selectedYear, setSelectedYear] = useState(() => {
    const requested = Number(new URLSearchParams(window.location.search).get("year"));
    return [2026, 2027].includes(requested) ? requested : 2026;
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("month");
    return requested ? Number(requested) : "";
  });
  const [activeTab, setActiveTab] = useState("monthly"); // "monthly" | "collaborators"

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = { year: selectedYear };
      if (selectedMonth) params.month = selectedMonth;
      const res = await getFinancialReconciliation(params);
      setData(res);
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible de charger la réconciliation financière."));
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const annualA = data?.annual_vision_a;
  const annualB = data?.annual_vision_b;
  const annualVar = data?.annual_variance;

  return (
    <div className="space-y-6 p-4 sm:p-6 text-white" data-testid="financial-reconciliation-page">
      {/* En-tête et navigation retour */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Link
              to={`/admin/pilotage/dashboard?year=${selectedYear}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-400 hover:text-white transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Pilotage
            </Link>
            <span className="text-slate-600">/</span>
            <span className="text-sm font-semibold text-slate-300">Rapprochement financier</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
              <Scale className="w-6 h-6 text-lenvia-blue" />
              Rapprochement Financier
            </h1>
            <span className="rounded-full border border-lenvia-blue/30 bg-lenvia-blue/10 px-2.5 py-0.5 text-xs font-semibold text-lenvia-blue">
              Double Calcul Consultatif (ADMIN)
            </span>
          </div>
        </div>

        {/* Sélecteurs Année et Mois */}
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border border-white/10 bg-slate-950/80 p-1 shadow-sm">
            {[2026, 2027].map((yr) => (
              <button
                key={yr}
                onClick={() => setSelectedYear(yr)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition cursor-pointer ${
                  selectedYear === yr
                    ? "bg-lenvia-blue text-white shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {yr}
              </button>
            ))}
          </div>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : "")}
            aria-label="Sélectionner le mois"
            className="rounded-xl border border-white/10 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm focus:border-lenvia-blue focus:outline-none cursor-pointer"
          >
            {MONTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-slate-900 text-white">
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bannière de cadrage métier et conformité */}
      <div className="rounded-2xl border border-lenvia-blue/30 bg-lenvia-blue/10 p-4 text-sm text-slate-200 flex items-start gap-3 shadow-sm backdrop-blur-sm">
        <HelpCircle className="w-5 h-5 text-lenvia-blue shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-white">Cadre d'analyse consultative (Étape 5) — Conformité financière</p>
          <p className="text-slate-300 text-xs mt-0.5 leading-relaxed">
            Ce tableau présente en miroir la <strong>Vision Actuelle</strong> (moteur de missions historique base 210 jours) et la{" "}
            <strong>Vision Enrichie</strong> (intégration des charges salariales permanentes des CDI, CDD et alternants).
            Les chiffres enrichis sont consultatifs et <em>n'altèrent en rien le P&amp;L comptable officiel de production</em>.
          </p>
        </div>
      </div>

      {/* Gestion Erreur & Chargement */}
      {error && <AppErrorState message={error} onRetry={fetchData} />}

      {loading && !error && (
        <div className="p-12 text-center text-slate-400 animate-pulse text-sm">
          Calcul du rapprochement financier en cours...
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Cartes KPI Synthétiques en Double Vision */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="reconciliation-summary-cards">
            {/* 1. CA Net (Invariant) */}
            <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 shadow-sm backdrop-blur-sm">
              <div className="flex items-center justify-between text-xs font-medium text-slate-400">
                <span>Chiffre d'Affaires Net</span>
                <span className="text-emerald-400 font-semibold flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Invariant
                </span>
              </div>
              <div className="mt-2 text-xl font-bold text-white">
                {formatCurrency(annualA?.ca_net)}
              </div>
              <p className="mt-1 text-xs text-slate-400">Identique sur les deux visions</p>
            </div>

            {/* 2. Coûts Totaux */}
            <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 shadow-sm backdrop-blur-sm">
              <div className="text-xs font-medium text-slate-400">Coûts Consultants &amp; Prestations</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <span className="text-xs text-slate-500 block">Actuel :</span>
                  <span className="text-base font-semibold text-slate-300">{formatCurrency(annualA?.total_consultant_cost)}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-lenvia-blue font-medium block">Enrichi :</span>
                  <span className="text-lg font-bold text-white">{formatCurrency(annualB?.total_consultant_cost)}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between text-xs">
                <span className="text-slate-400">Écart Coûts :</span>
                <span className={`font-semibold ${annualVar?.total_cost_delta > 0 ? "text-amber-400" : "text-emerald-400"}`}>
                  {formatDelta(annualVar?.total_cost_delta)}
                </span>
              </div>
            </div>

            {/* 3. Marge Brute */}
            <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 shadow-sm backdrop-blur-sm">
              <div className="text-xs font-medium text-slate-400">Marge Brute Globale</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <span className="text-xs text-slate-500 block">Actuelle :</span>
                  <span className="text-base font-semibold text-slate-300">{formatCurrency(annualA?.gross_margin)}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-lenvia-blue font-medium block">Enrichie :</span>
                  <span className="text-lg font-bold text-white">{formatCurrency(annualB?.gross_margin)}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between text-xs">
                <span className="text-slate-400">Taux Enrichi :</span>
                <span className="font-semibold text-white">{annualB?.margin_pct}%</span>
              </div>
            </div>

            {/* 4. Résultat Net */}
            <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 shadow-sm backdrop-blur-sm">
              <div className="text-xs font-medium text-slate-400">Résultat d'Exploitation</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <span className="text-xs text-slate-500 block">Actuel :</span>
                  <span className="text-base font-semibold text-slate-300">{formatCurrency(annualA?.net_result)}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-lenvia-blue font-medium block">Enrichi :</span>
                  <span className={`text-lg font-bold ${annualB?.net_result >= 0 ? "text-white" : "text-rose-400"}`}>
                    {formatCurrency(annualB?.net_result)}
                  </span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between text-xs">
                <span className="text-slate-400">Écart Résultat :</span>
                <span className="font-semibold text-white">{formatDelta(annualVar?.net_result_delta)}</span>
              </div>
            </div>
          </div>

          {/* Onglets de Navigation Vue Mensuelle / Vue Collaborateurs */}
          <div className="border-b border-white/10 flex gap-4">
            <button
              type="button"
              onClick={() => setActiveTab("monthly")}
              className={`pb-3 text-sm font-semibold border-b-2 transition cursor-pointer ${
                activeTab === "monthly"
                  ? "border-lenvia-blue text-lenvia-blue"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              Échéancier Mensuel (Double Colonne)
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("collaborators")}
              className={`pb-3 text-sm font-semibold border-b-2 transition cursor-pointer ${
                activeTab === "collaborators"
                  ? "border-lenvia-blue text-lenvia-blue"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              Détail par Collaborateur ({data.collaborator_rows?.length || 0})
            </button>
          </div>

          {/* TAB 1 : Échéancier Mensuel */}
          {activeTab === "monthly" && (
            <div className="rounded-2xl border border-white/10 bg-slate-900/60 shadow-sm overflow-hidden backdrop-blur-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-950/80 border-b border-white/10 text-slate-400 font-semibold uppercase tracking-wider">
                      <th className="py-3 px-4">Période</th>
                      <th className="py-3 px-4">CA Net</th>
                      <th className="py-3 px-4 text-slate-300 bg-slate-950/40">Coûts Actuel</th>
                      <th className="py-3 px-4 text-blue-200 bg-blue-950/30">Coûts Enrichi</th>
                      <th className="py-3 px-4">Écart Coûts</th>
                      <th className="py-3 px-4 text-slate-300 bg-slate-950/40">Marge Actuelle</th>
                      <th className="py-3 px-4 text-blue-200 bg-blue-950/30">Marge Enrichie</th>
                      <th className="py-3 px-4">Écart Marge</th>
                      <th className="py-3 px-4">Explication de l'Écart</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {data.monthly_rows.map((row) => (
                      <tr key={row.key} className="hover:bg-white/[0.02] transition">
                        <td className="py-3 px-4 font-semibold text-white">{row.month}</td>
                        <td className="py-3 px-4 text-slate-200">{formatCurrency(row.vision_a.ca_net)}</td>
                        <td className="py-3 px-4 bg-slate-950/40 text-slate-300 font-mono">
                          {formatCurrency(row.vision_a.total_consultant_cost)}
                        </td>
                        <td className="py-3 px-4 bg-blue-950/30 font-semibold text-blue-200 font-mono">
                          {formatCurrency(row.vision_b.total_consultant_cost)}
                        </td>
                        <td className="py-3 px-4 font-mono font-medium text-amber-400">
                          {formatDelta(row.variance.total_cost_delta)}
                        </td>
                        <td className="py-3 px-4 bg-slate-950/40 text-slate-300 font-mono">
                          {formatCurrency(row.vision_a.gross_margin)} ({row.vision_a.margin_pct}%)
                        </td>
                        <td className="py-3 px-4 bg-blue-950/30 font-semibold text-blue-200 font-mono">
                          {formatCurrency(row.vision_b.gross_margin)} ({row.vision_b.margin_pct}%)
                        </td>
                        <td className="py-3 px-4 font-mono font-semibold text-slate-300">
                          {formatDelta(row.variance.gross_margin_delta)}
                        </td>
                        <td className="py-3 px-4 text-slate-400 max-w-xs truncate" title={row.variance.explanation}>
                          {row.variance.explanation}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Ligne de Totaux Consolidés */}
                  <tfoot>
                    <tr className="bg-slate-950/90 font-bold border-t-2 border-white/10 text-white">
                      <td className="py-3 px-4">TOTAL {selectedYear}</td>
                      <td className="py-3 px-4">{formatCurrency(annualA?.ca_net)}</td>
                      <td className="py-3 px-4 font-mono text-slate-200">{formatCurrency(annualA?.total_consultant_cost)}</td>
                      <td className="py-3 px-4 font-mono text-blue-200">{formatCurrency(annualB?.total_consultant_cost)}</td>
                      <td className="py-3 px-4 font-mono text-amber-400">{formatDelta(annualVar?.total_cost_delta)}</td>
                      <td className="py-3 px-4 font-mono text-slate-200">{formatCurrency(annualA?.gross_margin)} ({annualA?.margin_pct}%)</td>
                      <td className="py-3 px-4 font-mono text-blue-200">{formatCurrency(annualB?.gross_margin)} ({annualB?.margin_pct}%)</td>
                      <td className="py-3 px-4 font-mono">{formatDelta(annualVar?.gross_margin_delta)}</td>
                      <td className="py-3 px-4 text-xs font-normal text-slate-400">Somme stricte des 12 mois</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* TAB 2 : Détail par Collaborateur */}
          {activeTab === "collaborators" && (
            <div className="rounded-2xl border border-white/10 bg-slate-900/60 shadow-sm overflow-hidden backdrop-blur-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-950/80 border-b border-white/10 text-slate-400 font-semibold uppercase tracking-wider">
                      <th className="py-3 px-4">Collaborateur</th>
                      <th className="py-3 px-4">Contrat</th>
                      <th className="py-3 px-4">Statut Opérationnel</th>
                      <th className="py-3 px-4">Salaire Annuel</th>
                      <th className="py-3 px-4 text-slate-300 bg-slate-950/40">Coût Actuel</th>
                      <th className="py-3 px-4 text-blue-200 bg-blue-950/30">Coût Enrichi</th>
                      <th className="py-3 px-4">Écart Coût</th>
                      <th className="py-3 px-4 text-slate-300 bg-slate-950/40">Marge Actuelle</th>
                      <th className="py-3 px-4 text-blue-200 bg-blue-950/30">Marge Enrichie</th>
                      <th className="py-3 px-4">Justification de l'Écart</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {data.collaborator_rows.map((row) => (
                      <tr key={row.collaborator_id} className="hover:bg-white/[0.02] transition">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-white">{row.collaborator_name}</div>
                          <div className="text-slate-400 text-[10px]">{row.collaborator_email}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-[10px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded border border-white/5">
                            {row.contract_kind}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                              row.operational_status === "EN_MISSION"
                                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                : row.operational_status === "INTERCONTRAT_SALARIE"
                                ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                                : "bg-slate-800 text-slate-400 border border-white/5"
                            }`}
                          >
                            {row.operational_status_label}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-slate-300">
                          {row.annual_salary ? formatCurrency(row.annual_salary) : "—"}
                        </td>
                        <td className="py-3 px-4 bg-slate-950/40 text-slate-300 font-mono">
                          {formatCurrency(row.cost_a)}
                        </td>
                        <td className="py-3 px-4 bg-blue-950/30 font-semibold text-blue-200 font-mono">
                          {formatCurrency(row.cost_b)}
                        </td>
                        <td className="py-3 px-4 font-mono font-medium text-amber-400">
                          {formatDelta(row.cost_delta)}
                        </td>
                        <td className="py-3 px-4 bg-slate-950/40 text-slate-300 font-mono">
                          {formatCurrency(row.margin_a)}
                        </td>
                        <td className="py-3 px-4 bg-blue-950/30 font-semibold text-blue-200 font-mono">
                          {formatCurrency(row.margin_b)}
                        </td>
                        <td className="py-3 px-4 text-slate-400 max-w-sm truncate" title={row.explanation}>
                          {row.explanation}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
